"use client";

import * as React from "react";
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createApiClient,
  type ApiClient,
  type AuthSession,
  type LoginResult,
  type SessionUser,
  type WorkspaceSummary,
} from "./client";

const ApiClientContext = React.createContext<ApiClient | null>(null);
const SessionContext = React.createContext<{
  user: SessionUser | null;
  /** Presence sentinel `"cookie"` when authenticated (JWT is httpOnly). */
  accessToken: string | null;
  refreshToken: string | null;
  workspaceId: string | null;
  workspaces: WorkspaceSummary[];
  applyAuth: (result: AuthSession) => Promise<SessionUser>;
  clearSession: () => void;
  logout: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  setWorkspaceId: (id: string) => void;
  setWorkspaces: (list: WorkspaceSummary[]) => void;
} | null>(null);

const STORAGE_SESSION = "lwp.session";
const STORAGE_WORKSPACE = "lwp.workspaceId";
const STORAGE_REALM = "lwp.realm";
const STORAGE_CSRF = "lwp.csrf";
const STORAGE_ACCESS_EXPIRES = "lwp.accessExpiresAt";

/** Clear legacy token keys from older builds. */
const LEGACY_TOKEN_KEYS = ["lwp.accessToken", "lwp.refreshToken"] as const;

function readStorage(key: string): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(key);
}

function writeStorage(key: string, value: string) {
  localStorage.setItem(key, value);
}

function removeStorage(key: string) {
  localStorage.removeItem(key);
}

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function isTokenPair(result: LoginResult): result is AuthSession {
  return "csrfToken" in result && typeof result.csrfToken === "string";
}

function parseExpiresInToMs(expiresIn: string, fromMs = Date.now()): number {
  const m = /^(\d+)([smhd])$/i.exec(expiresIn.trim());
  if (!m) return fromMs + 15 * 60_000;
  const n = Number(m[1]);
  const unit = m[2]!.toLowerCase();
  const mult =
    unit === "s"
      ? 1000
      : unit === "m"
        ? 60_000
        : unit === "h"
          ? 3_600_000
          : 86_400_000;
  return fromMs + n * mult;
}

export function ApiProvider({
  baseUrl,
  children,
}: {
  baseUrl: string;
  children: React.ReactNode;
}) {
  const [queryClient] = React.useState(createQueryClient);
  const csrfRef = React.useRef<string | null>(readStorage(STORAGE_CSRF));
  const workspaceRef = React.useRef<string | null>(
    readStorage(STORAGE_WORKSPACE),
  );
  const realmRef = React.useRef<"staff" | "client" | null>(
    (readStorage(STORAGE_REALM) as "staff" | "client" | null) ?? null,
  );
  const accessExpiresAtRef = React.useRef<number | null>(
    (() => {
      const raw = readStorage(STORAGE_ACCESS_EXPIRES);
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    })(),
  );

  const [accessToken, setAccessToken] = React.useState<string | null>(() =>
    readStorage(STORAGE_SESSION) === "1" ? "cookie" : null,
  );
  const [user, setUser] = React.useState<SessionUser | null>(null);
  const [workspaceId, setWorkspaceIdState] = React.useState<string | null>(() =>
    readStorage(STORAGE_WORKSPACE),
  );
  const [workspaces, setWorkspaces] = React.useState<WorkspaceSummary[]>([]);

  const persistSessionRef = React.useRef<(pair: AuthSession) => void>(() => {});
  const clearSessionRef = React.useRef<() => void>(() => {});

  const clearSession = React.useCallback(() => {
    csrfRef.current = null;
    workspaceRef.current = null;
    realmRef.current = null;
    accessExpiresAtRef.current = null;
    setAccessToken(null);
    setUser(null);
    setWorkspaces([]);
    setWorkspaceIdState(null);
    removeStorage(STORAGE_SESSION);
    removeStorage(STORAGE_WORKSPACE);
    removeStorage(STORAGE_REALM);
    removeStorage(STORAGE_CSRF);
    removeStorage(STORAGE_ACCESS_EXPIRES);
    for (const key of LEGACY_TOKEN_KEYS) removeStorage(key);
    queryClient.clear();
  }, [queryClient]);

  const client = React.useMemo(
    () =>
      createApiClient({
        baseUrl,
        getAuthRealm: () => realmRef.current,
        getWorkspaceId: () => workspaceRef.current,
        getCsrfToken: () => csrfRef.current,
        onCsrfSynced: (token) => {
          csrfRef.current = token;
          writeStorage(STORAGE_CSRF, token);
        },
        hasSession: () => readStorage(STORAGE_SESSION) === "1",
        accessNearExpiry: () => {
          const at = accessExpiresAtRef.current;
          if (at == null) return false;
          return Date.now() >= at - 60_000;
        },
        onTokensRefreshed: (pair) => {
          persistSessionRef.current(pair);
        },
        onUnauthorized: (path, meta) => {
          if (
            meta?.refreshFailed ||
            path === "/v1/settings/session" ||
            path.startsWith("/v1/auth/")
          ) {
            clearSessionRef.current();
          }
        },
      }),
    [baseUrl],
  );

  const persistSession = React.useCallback((pair: AuthSession) => {
    csrfRef.current = pair.csrfToken;
    realmRef.current = pair.user.realm;
    const expiresAt = parseExpiresInToMs(pair.expiresIn);
    accessExpiresAtRef.current = expiresAt;
    setAccessToken("cookie");
    writeStorage(STORAGE_SESSION, "1");
    writeStorage(STORAGE_REALM, pair.user.realm);
    writeStorage(STORAGE_CSRF, pair.csrfToken);
    writeStorage(STORAGE_ACCESS_EXPIRES, String(expiresAt));
    for (const key of LEGACY_TOKEN_KEYS) removeStorage(key);
    if (pair.activeWorkspaceId) {
      workspaceRef.current = pair.activeWorkspaceId;
      setWorkspaceIdState(pair.activeWorkspaceId);
      writeStorage(STORAGE_WORKSPACE, pair.activeWorkspaceId);
    }
  }, []);
  persistSessionRef.current = persistSession;
  clearSessionRef.current = clearSession;

  const syncCsrfFromSession = React.useCallback((session: SessionUser) => {
    if (typeof session.csrfToken === "string" && session.csrfToken) {
      csrfRef.current = session.csrfToken;
      writeStorage(STORAGE_CSRF, session.csrfToken);
    }
  }, []);

  const bootstrapUser = React.useCallback(
    async (pair: AuthSession): Promise<SessionUser> => {
      let session = await client.auth.session();
      syncCsrfFromSession(session);
      if (pair.user.realm === "client") {
        try {
          const membership = await client.logistics.clientOrganizations.me();
          session = {
            ...session,
            clientOrganizationId: membership.clientOrganizationId,
            clientOrgRole: membership.role,
            clientOrgEngagement: membership.engagement,
            permittedEndCustomerIds: membership.permittedEndCustomerIds,
            permittedStoreIds: membership.permittedStoreIds,
          };
        } catch {
          /* membership optional until linked */
        }
      }
      setUser(session);
      if (pair.user.realm === "staff") {
        setWorkspaces(await client.workspaces.list());
      }
      return session;
    },
    [client, syncCsrfFromSession],
  );

  const applyAuth = React.useCallback(
    async (result: AuthSession) => {
      persistSession(result);
      return bootstrapUser(result);
    },
    [persistSession, bootstrapUser],
  );

  React.useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    void client.auth.session().then(
      async (me) => {
        if (cancelled) return;
        syncCsrfFromSession(me);
        let next = me;
        if (me.realm === "client") {
          try {
            const membership = await client.logistics.clientOrganizations.me();
            next = {
              ...me,
              clientOrganizationId: membership.clientOrganizationId,
              clientOrgRole: membership.role,
              clientOrgEngagement: membership.engagement,
              permittedEndCustomerIds: membership.permittedEndCustomerIds,
              permittedStoreIds: membership.permittedStoreIds,
            };
          } catch {
            /* ignore */
          }
        }
        if (!cancelled) {
          setUser(next);
          if (me.realm === "staff") {
            setWorkspaces(await client.workspaces.list());
          }
        }
      },
      () => {
        if (!cancelled) clearSession();
      },
    );
    return () => {
      cancelled = true;
    };
  }, [accessToken, client, clearSession, syncCsrfFromSession]);

  const logout = React.useCallback(async () => {
    const realm = realmRef.current ?? undefined;
    try {
      await client.auth.logout(realm ? { realm } : {});
    } catch {
      /* still clear local */
    }
    clearSession();
  }, [client, clearSession]);

  const setWorkspaceId = React.useCallback(
    (id: string) => {
      workspaceRef.current = id;
      setWorkspaceIdState(id);
      writeStorage(STORAGE_WORKSPACE, id);
      queryClient.clear();
    },
    [queryClient],
  );

  const switchWorkspace = React.useCallback(
    async (id: string) => {
      const pair = await client.workspaces.switch({ workspaceId: id });
      persistSession(pair);
      queryClient.clear();
      await bootstrapUser(pair);
    },
    [client, persistSession, bootstrapUser, queryClient],
  );

  const sessionValue = React.useMemo(
    () => ({
      user,
      accessToken,
      refreshToken: null,
      workspaceId,
      workspaces,
      applyAuth,
      clearSession,
      logout,
      switchWorkspace,
      setWorkspaceId,
      setWorkspaces,
    }),
    [
      user,
      accessToken,
      workspaceId,
      workspaces,
      applyAuth,
      clearSession,
      logout,
      switchWorkspace,
      setWorkspaceId,
    ],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientContext.Provider value={client}>
        <SessionContext.Provider value={sessionValue}>
          {children}
        </SessionContext.Provider>
      </ApiClientContext.Provider>
    </QueryClientProvider>
  );
}

export function useApiClient() {
  const ctx = React.useContext(ApiClientContext);
  if (!ctx) throw new Error("useApiClient requires ApiProvider");
  return ctx;
}

export function useSession() {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error("useSession requires ApiProvider");
  return ctx;
}

export function useAppQueryClient() {
  return useQueryClient();
}

/**
 * WorkspaceSwitcher — Logistics Branch picker.
 * Calls POST /workspaces/switch and replaces AuthSession cookies (AGENTS.md).
 */
export function WorkspaceSwitcher({
  label = "Branch",
  variant = "default",
}: {
  label?: string;
  variant?: "default" | "sidebar";
}) {
  const { workspaces, workspaceId, switchWorkspace } = useSession();
  const [busy, setBusy] = React.useState(false);
  const current =
    workspaces.find((w) => w.id === workspaceId) ?? workspaces[0] ?? null;

  if (variant === "sidebar") {
    if (!current) return null;
    if (workspaces.length <= 1) {
      return (
        <div className="flex w-full items-center justify-between gap-3 px-2 py-2">
          <span className="shrink-0 text-xs text-tertiary">{label}</span>
          <span className="min-w-0 truncate text-right text-[13px] font-medium text-foreground">
            {current.name}
          </span>
        </div>
      );
    }
    return (
      <label className="flex w-full items-center justify-between gap-3 px-2 py-2">
        <span className="shrink-0 text-xs text-tertiary">{label}</span>
        <select
          className="h-8 min-w-0 max-w-[70%] cursor-pointer truncate rounded-md border border-border bg-background px-2 text-right text-xs font-medium text-foreground transition-colors duration-micro hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          value={workspaceId ?? current.id}
          disabled={busy}
          aria-label={label}
          onChange={(e) => {
            const id = e.target.value;
            setBusy(true);
            void switchWorkspace(id).finally(() => setBusy(false));
          }}
        >
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (workspaces.length <= 1) return null;

  return (
    <label className="block space-y-1 text-xs text-muted-foreground">
      <span>{label}</span>
      <select
        className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground"
        value={workspaceId ?? ""}
        disabled={busy}
        onChange={(e) => {
          const id = e.target.value;
          setBusy(true);
          void switchWorkspace(id).finally(() => setBusy(false));
        }}
      >
        {workspaces.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </select>
    </label>
  );
}
